import { useState, useRef, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext.jsx';
import { ai, getToken } from '../api/client.js';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function SmartSearch({ onClose, onSelectFont }) {
  const { theme } = useTheme();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [fontsReady, setFontsReady] = useState({});
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Load fonts via FontFace API, trigger re-render when each is ready
  useEffect(() => {
    if (!results.length) return;
    setFontsReady({});

    results.forEach(async (f, idx) => {
      try {
        if (f.source === 'google-fonts') {
          // Load directly from Google's CDN (already allowed by font-src CSP)
          if (!f.regularUrl) {
            setFontsReady(prev => ({ ...prev, [idx]: true }));
            return;
          }
          const fontFace = new FontFace(f.family, `url(${f.regularUrl})`);
          const loaded = await fontFace.load();
          document.fonts.add(loaded);
        } else if (f.source === 'adobe-fonts') {
          // Typekit CSS is already injected globally; just wait for availability
          await document.fonts.load(`1em '${f.family}'`).catch(() => {});
        } else {
          // Uploaded font — fetch binary and load via blob URL
          if (!f.preview_file_id) return;
          const resp = await fetch(
            `${API_BASE}/fonts/${f.id}/serve/${f.preview_file_id}`,
            { headers: { Authorization: `Bearer ${getToken()}` } }
          );
          if (!resp.ok) return;
          const blob = await resp.blob();
          const blobUrl = URL.createObjectURL(blob);
          const fontFace = new FontFace(f.family, `url(${blobUrl})`);
          const loaded = await fontFace.load();
          document.fonts.add(loaded);
        }
        setFontsReady(prev => ({ ...prev, [idx]: true }));
      } catch (_) {}
    });
  }, [results]);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setSearched(true);
    try {
      const data = await ai.smartSearch(query.trim());
      setResults(data.fonts || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        paddingTop: 80,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 560, maxHeight: '70vh',
          background: theme.bgElevated,
          border: `1px solid ${theme.borderHover}`,
          borderRadius: 16, boxShadow: '0 24px 80px rgba(0,0,0,0.35)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Search Input */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '16px 20px',
          borderBottom: `1px solid ${theme.borderLight}`,
        }}>
          <span style={{
            fontSize: 18, lineHeight: 1,
            background: 'linear-gradient(135deg, #7C5CFC, #4F8EF7)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>&#10024;</span>
          <input
            ref={inputRef}
            type="text"
            placeholder="Describe what you're looking for... e.g. clean geometric sans for tech brand"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            style={{
              flex: 1, background: 'none', border: 'none',
              color: theme.text, fontSize: 14, outline: 'none',
              fontFamily: 'inherit',
            }}
          />
          <button
            onClick={handleSearch}
            disabled={loading || !query.trim()}
            style={{
              padding: '6px 16px', fontSize: 12, fontWeight: 600,
              background: query.trim() ? 'linear-gradient(135deg, #4F8EF7, #7C5CFC)' : theme.bgInput,
              border: 'none', borderRadius: 8,
              color: query.trim() ? '#fff' : theme.textFaint,
              cursor: query.trim() ? 'pointer' : 'default',
              fontFamily: 'inherit',
            }}
          >{loading ? 'Searching...' : 'Search'}</button>
        </div>

        {/* Results */}
        <div style={{
          flex: 1, overflowY: 'auto', padding: '12px 16px',
        }}>
          {loading && (
            <div style={{
              textAlign: 'center', padding: 32, color: theme.textMuted, fontSize: 13,
            }}>
              <div style={{
                width: 24, height: 24, border: `2px solid ${theme.borderMedium}`,
                borderTopColor: theme.accent, borderRadius: '50%',
                margin: '0 auto 12px',
                animation: 'spin 0.8s linear infinite',
              }} />
              AI is analyzing your font library...
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {!loading && searched && results.length === 0 && (
            <div style={{
              textAlign: 'center', padding: 32, color: theme.textFaint, fontSize: 13,
            }}>No matching fonts found. Try a different description.</div>
          )}

          {!loading && results.map((f, idx) => (
            <button
              key={`${f.source}:${f.family}`}
              onClick={() => { if (f.id) onSelectFont(f.id); onClose(); }}
              style={{
                width: '100%', padding: '12px 14px', marginBottom: 6,
                background: theme.bgTertiary,
                border: `1px solid ${theme.borderLight}`,
                borderRadius: 10, cursor: 'pointer',
                textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 6,
                fontFamily: 'inherit',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = theme.accentBg;
                e.currentTarget.style.borderColor = theme.accentBorder;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = theme.bgTertiary;
                e.currentTarget.style.borderColor = theme.borderLight;
              }}
            >
              {/* Font preview in actual font face */}
              <div style={{
                fontFamily: `'${f.family}', serif`,
                fontSize: 28, lineHeight: 1.3,
                color: theme.textSecondary,
                overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                opacity: fontsReady[idx] ? 1 : 0.3,
                transition: 'opacity 0.3s',
              }}>
                The quick brown fox
              </div>

              {/* Metadata row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: theme.text }}>{f.family}</span>
                <span style={{
                  fontSize: 9, fontWeight: 600, color: theme.textGhost,
                  background: theme.badgeBg, padding: '1px 6px', borderRadius: 3,
                  textTransform: 'uppercase',
                }}>{f.category}</span>
                {f.source === 'google-fonts' && (
                  <span style={{
                    fontSize: 9, fontWeight: 600, color: '#4285F4',
                    background: '#4285F418', padding: '1px 6px', borderRadius: 3,
                    textTransform: 'uppercase',
                  }}>Google</span>
                )}
                {f.source === 'adobe-fonts' && (
                  <span style={{
                    fontSize: 9, fontWeight: 600, color: '#FA0F00',
                    background: '#FA0F0018', padding: '1px 6px', borderRadius: 3,
                    textTransform: 'uppercase',
                  }}>Adobe</span>
                )}
                <span style={{
                  fontSize: 11, color: theme.accent,
                  background: theme.accentBg,
                  padding: '2px 8px', borderRadius: 4,
                }}>{f.reason}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Footer hint */}
        <div style={{
          padding: '10px 20px', borderTop: `1px solid ${theme.borderLight}`,
          fontSize: 10, color: theme.textGhost, textAlign: 'center',
        }}>
          Powered by AI &middot; Press Esc to close
        </div>
      </div>
    </div>
  );
}
